
import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { SidebarProvider, Sidebar, SidebarContent, SidebarInset } from "@/components/ui/sidebar";
import { GuestSidebar } from "@/components/guest/GuestSidebar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import TopBar from "@/components/TopBar";
import { ErrorBoundary } from "@/components/admin/ErrorBoundary";
import { useUser } from "@/contexts/UserContext";

// Lazy load guest pages for better performance
const GuestDashboardPage = lazy(() => import("@/pages/guest/GuestDashboardPage"));
const GuestBookingsPage = lazy(() => import("@/pages/guest/GuestBookingsPage"));
const GuestMessagesPage = lazy(() => import("@/pages/guest/GuestMessagesPage"));
const GuestPreferencesPage = lazy(() => import("@/pages/guest/GuestPreferencesPage"));
const GuestReviewsPage = lazy(() => import("@/pages/guest/GuestReviewsPage"));
const GuestWishlistsPage = lazy(() => import("@/pages/guest/GuestWishlistsPage"));
const GuestSettingsPage = lazy(() => import("@/pages/guest/GuestSettingsPage"));

const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-[300px]">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

const GuestLayout = () => {
  const isMobile = useIsMobile();
  const { user } = useUser();

  return (
    <SidebarProvider>
      <div className="flex flex-col min-h-screen">
        <TopBar />
        <div className="flex flex-1">
          {isMobile ? (
            <Sheet>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="fixed top-4 right-4 z-50 md:hidden"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80%] sm:w-[300px] p-0">
                <div className="h-full overflow-auto py-6">
                  <GuestSidebar />
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <Sidebar className="border-r border-gray-200 hidden md:block">
              <SidebarContent>
                <GuestSidebar />
              </SidebarContent>
            </Sidebar>
          )}
          <SidebarInset className="flex-1">
            <div className="flex-1 px-4 md:px-6 py-4 md:py-6">
              {isMobile && <div className="h-12"></div>} {/* Spacer for mobile menu button */}
              
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<GuestDashboardPage />} />
                    <Route path="/bookings" element={<GuestBookingsPage />} />
                    <Route path="/messages" element={<GuestMessagesPage />} />
                    <Route path="/preferences" element={<GuestPreferencesPage />} />
                    <Route path="/reviews" element={<GuestReviewsPage />} />
                    <Route path="/wishlists" element={<GuestWishlistsPage />} />
                    <Route path="/settings" element={<GuestSettingsPage />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </div>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default GuestLayout;
